import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Badge, Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import venderService from "@/service/vender.service";
import { setFilters, setVendors, setLoading, setScrollY } from "@/redux/vendor.search.slice";
import Loader from "@/components/Loader";
import "./styles.css"
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { vendorType } from "@/constants/vendorType";
import useLocationAutocomplete from "@/hooks/useLocationAutocomplete";
import { Paper } from "@mui/material";
import HBNVerified from "../../../../assets/HBN-Verified.svg";
import { useNavigate } from 'react-router-dom';
import LoginPopup from "@/common/LoginPopup";
import chatService from "@/service/chat.service";
import { Bell, Search, MapPin, Star, MessageCircle, Users, Sparkles, Plus } from "lucide-react"
import RequirementsDialog from "@/modules/vendor/components/RequirementsDialog";

function SearchVendor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = Boolean(localStorage.getItem("access_token"));
  const currentLocation = useSelector((state) => state.auth.currentLocation);
  const { filters, vendors, loading, scrollY } = useSelector((state) => state.searchVendor);
  const [selectedService, setSelectedService] = useState("");
  const [showSuggestions, setShowSuggestions] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showPopupRequirementsSection, setShowRequirementsSectionPopup] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalVendor, setTotalVendor] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [redirectUrl, setRedirectUrl] = useState("");
  const containerRef = useRef < HTMLDivElement > (null);
  const {
    query,
    suggestions,
    error,
    handleInputChange,
    setSuggestions,
    handleSelectPlace,
    handleClear,
    placeDetails,
    placeLoading,
    setQuery,
  } = useLocationAutocomplete({
    onLocationSelect: (location) => {
      console.log("Selected location:", location);
    }
  });

  useLayoutEffect(() => {
    const stored = localStorage.getItem("searchVendorScrollY");
    const y = stored ? parseInt(stored, 10) : 0;

    if (containerRef.current) {
      containerRef.current.scrollTop = y;
    } else {
      window.scrollTo(0, y);
    }

    // Remove on next tick, after the browser has done the scroll
    setTimeout(() => {
      localStorage.removeItem("searchVendorScrollY");
    }, 0);
  }, []);

  useEffect(() => {
    if (filters?.vendorType?.length > 0) {
      setSelectedService(filters.vendorType[0]);
    } else {
      setSelectedService("");
    }
  }, [filters?.vendorType]);

  // Fetch Vendors
  const fetchVendors = useCallback(async (reset = false) => {
    try {
      setShowSuggestions(localStorage.getItem("homePage"));
      dispatch(setLoading(true));

      const storedLocation = localStorage.getItem("selectedVendorLocation");
      let locationData = storedLocation ? JSON.parse(storedLocation) : null;

      if (!(locationData?.address)) {
        locationData = currentLocation;
      }

      const { postcode = "", city = "", address = "", latitude = "", longitude = "" } = locationData || {};

      if (locationData?.address) setQuery(locationData.address);

      const vendorList = await venderService.fetchVendorsByLocation(
        postcode,
        city,
        latitude,
        longitude,
        filters,
        page,
        limit
      );

      const vendorArray = Array.isArray(vendorList?.vendors)
        ? vendorList?.vendors
        : vendorList?.vendors?.data || [];

      setTotalVendor(vendorList?.total || 0);
      if (reset) {
        dispatch(setVendors(vendorArray));
      } else {
        dispatch(setVendors([...vendors, ...vendorArray]));
      }

      setHasMore(vendorArray.length >= limit);
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, currentLocation, filters, page, limit, vendors, setQuery]);

  useEffect(() => {
    if (totalVendor === 0) {
      const timer = setTimeout(() => {
        setShowRequirementsSectionPopup(true);
      }, 2000);

      return () => clearTimeout(timer); // cleanup
    }
  }, [totalVendor]);

  // Initial Load & When Filters Change
  useEffect(() => {
    setPage(1);
    fetchVendors(true);
  }, [filters]);

  // Infinite Scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 200 >=
        document.documentElement.scrollHeight &&
        !loading &&
        hasMore
      ) {
        setPage((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore]);

  // Fetch next page when page changes
  useEffect(() => {
    if (page > 1) fetchVendors();
  }, [page]);

  const handleSearch = () => {
    dispatch(setFilters({
      vendorType: selectedService ? [selectedService] : [],
      rating: null,
      serviceType: [],
    }));
    setPage(1);
  };

  const [isContactFormOpen, setIsContactFormOpen] = useState(false)
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleContactSubmit = async () => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: contactForm.name,
        email: contactForm.email,
        phone_number: contactForm.phone,
        requirements: contactForm.message,
      }

      await customerService.createLeadRequest(payload)

      // Reset form and close modal
      setContactForm({ name: "", email: "", phone: "", message: "" })
      setIsContactFormOpen(false)

      alert("✅ Thank you! Your requirements have been submitted successfully.")
    } catch (error) {
      console.error("Error submitting form:", error)
      alert("Sorry, there was an error submitting your requirements. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const isContactFormValid = contactForm.name && contactForm.email && contactForm.message

  const claimVendor = async (vendorId) => {
    try {
      const res = await venderService.claimVendorById(vendorId);
      if (res?.status) {
        // Update vendor in Redux list
        const updatedVendors = vendors.map(v =>
          v._id === vendorId ? { ...v, status: "claimed" } : v
        );
        dispatch(setVendors(updatedVendors));
      }
    } catch (err) {
      console.error("Error claiming vendor:", err);
    }
  };
  const clickInputChange = () => {
    setShowSuggestions("true");
    localStorage.removeItem("homePage");
  };
  const handleChatClick = async (VendorDetail) => {
    if (!VendorDetail?._id) {
      console.error("Vendor ID is missing!");
      return;
    }
    if (!isAuthenticated) {
      setRedirectUrl("/customer/auth/login");
      setShowPopup(true);
    } else {
      await chatService.checkConversation(VendorDetail._id);
      navigate("/customer/inbox", { state: { vendorId: VendorDetail._id } });
    }
  };
  const handleProfileClick = (vendor) => {
    navigate(`/customer/search-brokers/${vendor._id}`);
  };

  return (
    <>
      <div className="min-h-screen relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgb(106, 0, 244), rgb(111, 109, 248))",
        }}>
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-20 w-60 h-60 bg-white/3 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-white/4 rounded-full blur-xl animate-pulse delay-500"></div>
        </div>

        {/* Compact Hero Section */}
        <section className="relative py-6">
          {/* Hero Background Image with Overlay */}
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-60"
              style={{
                backgroundImage: "url('/images/melbourne-cityscape.jpg')",
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#6A00F4]/75 via-[#7C3AED]/70 to-[#6F6DF8]/75"></div>
          </div>
          <div className="relative z-10">
            <div className="container mx-auto px-4 sm:px-6">
              <div className="text-center mb-5">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 leading-tight">
                  Find Qualified <span style={{ color: "#f1c40f" }}>Professionals</span>, Hassle-Free.
                </h1>
              </div>

              {/* Compact Glassy Search Form */}
              <div className="max-w-6xl mx-auto">
                <div className="bg-white/15 backdrop-blur-xl rounded-2xl p-4 shadow-2xl border border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                      <Select value={selectedService} onValueChange={(value) => setSelectedService(value === "none" ? "" : value)}>
                        <SelectTrigger className="w-full h-12 bg-white/30 text-white placeholder-white rounded-lg text-base px-4">
                          <SelectValue placeholder="I'm looking for ..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">All</SelectItem>
                          {vendorType.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="relative">
                      {/* <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70 h-4 w-4" /> */}
                      <Input
                        type="text"
                        placeholder="Enter your suburb or postcode"
                        value={query}
                        onChange={(e) => {
                          clickInputChange();
                          handleInputChange(e);
                        }}
                        className="w-full h-12 bg-white/30 text-white placeholder-white rounded-lg text-base px-4"
                      />
                      {query && (
                        <button
                          onClick={() => {
                            clickInputChange();
                            handleClear();
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white"
                        >
                          ✖
                        </button>
                      )}
                      {showSuggestions != "false" && suggestions.length > 0 && !placeDetails && (
                        <Paper
                          elevation={3}
                          className="absolute w-full mt-2 max-h-60 overflow-y-auto bg-white rounded-lg shadow-lg z-50"
                        >
                          {suggestions.map((place, index) => (
                            <div
                              key={index}
                              onClick={() => handleSelectPlace(place)}
                              className="p-2 cursor-pointer hover:bg-gray-100"
                            >
                              {place}
                            </div>
                          ))}
                        </Paper>
                      )}
                      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                      {loading && <p className="text-gray-300 text-sm mt-2">Loading...</p>}
                    </div>

                    <Button
                      onClick={handleSearch}
                      className="h-[48px] bg-gradient-to-r from-[#6A00F4] to-[#6F6DF8] hover:from-[#5A00E4] hover:to-[#5F5DE8] text-white font-semibold shadow-lg border border-white/20">
                      <Search className="mr-2 h-4 w-4" />
                      Search
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-6">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-white drop-shadow-lg mb-1">Search Results</h2>
              <p className="text-white font-medium drop-shadow-md text-sm">Found {totalVendor} <span className="text-yellow-300 font-semibold">professionals</span>  in your area</p>
            </div>

            <div className="space-y-3">
              {page === 1 && loading ? (
                <Typography variant="body1" sx={{ textAlign: "center" }}>
                  <Loader />
                </Typography>
              ) : vendors.length > 0 ? (
                <>
                  {vendors.map((vendor) => (

                    <Card className="relative z-20 bg-[#D8C7F0] border border-white/20 shadow-xl hover:bg-[#E0D0F5] transition-all duration-300 hover:scale-[1.01] animate-fade-in">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-14 w-14 border-2 border-white/30 flex-shrink-0 cursor-pointer" onClick={() => handleProfileClick(vendor)}>
                            {vendor.profile_picture?.file_url ? (
                              <img
                                src={vendor.profile_picture.file_url}
                                alt={vendor.full_name}
                                className="h-full w-full object-cover rounded-full"
                              />
                            ) : (
                              <AvatarFallback className="bg-gradient-to-br from-[#6A00F4] to-[#6F6DF8] text-white font-bold text-lg cursor-pointer">
                                {vendor.first_name?.[0]}
                                {vendor.last_name?.[0]}
                              </AvatarFallback>
                            )}
                          </Avatar>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 gap-2">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 cursor-pointer" onClick={() => handleProfileClick(vendor)}>
                                    {vendor.first_name + " " + vendor.last_name}
                                  </h3>
                                  {vendor.status === 'in_complete' && (
                                    <Button
                                      onClick={() => claimVendor(vendor._id)}
                                      sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        px: 1.5,
                                        py: 0.5,
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        minWidth: 120,
                                        '&:hover': {
                                          bgcolor: '#f0f0f0',
                                        },
                                      }}
                                    >
                                      Claim
                                    </Button>
                                  )}

                                  {(vendor.status === 'claimed' || vendor.status === 'pending') && (
                                    <Button
                                      disabled
                                      sx={{
                                        bgcolor: 'white',
                                        color: 'grey.500',
                                        border: '1px solid',
                                        borderColor: 'grey.400',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        px: 1.5,
                                        py: 0.5,
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        minWidth: 120,
                                        '&.Mui-disabled': {
                                          color: 'grey.600',
                                          borderColor: 'grey.400',
                                        },
                                      }}
                                    >
                                      Claimed
                                    </Button>
                                  )}
                                  {vendor.status === 'approved' && (
                                    <img src={HBNVerified} alt="HBN Verified" style={{ height: 35 }} />
                                  )}
                                </div>

                                <p className="font-medium text-sm" style={{ color: "black", fontWeight: "600" }}>
                                  {vendor.vendor_type || vendor.professional_title}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 sm:text-right">
                                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                <span className="font-bold text-gray-800">{vendor.vendorRating != null ? vendor.vendorRating.toFixed(1) : "0.0"}</span>
                                <span className="text-gray-600 text-sm">({vendor.totalReviews || 0})</span>
                              </div>
                            </div>
                            <p className="text-gray-700 text-sm mb-3">
                              {vendor.description}
                            </p>

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex items-center gap-1 text-xs text-gray-600">
                                <MapPin className="h-3 w-3" />
                                {vendor?.address ? (
                                  vendor.address
                                ) : (
                                  <span className="italic text-gray-400">Address will be updated soon</span>
                                )}
                              </div>
                              <div className="flex gap-2">
                                {vendor.status === 'approved' && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="bg-gradient-to-r from-[#6A00F4] to-[#6F6DF8] hover:from-[#5A00E4] hover:to-[#5F5DE8] text-white text-xs px-3 py-1.5 h-8 cursor-pointer"
                                      onClick={() => handleChatClick(vendor)}
                                    >
                                      <MessageCircle className="mr-1 h-3 w-3" />
                                      Chat
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-gray-400 text-gray-700 hover:bg-gray-100 text-xs px-3 py-1.5 h-8 bg-transparent cursor-pointer"
                                  onClick={() => handleProfileClick(vendor)}
                                >
                                  Profile
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {loading && page > 1 && (
                    <div className="text-center py-3">
                      <Loader />
                    </div>
                  )}
                </>
              ) : (

                // <Box
                //   sx={{
                //     mt: 4,
                //     p: 3,
                //     backgroundColor: "rgba(255, 255, 255, 0.1)",
                //     borderRadius: "8px",
                //     border: "1px solid rgba(255,255,255,0.2)",
                //     textAlign: "center",
                //   }}
                // >
                //   <Typography variant="body1" color="white">
                //     No vendors found for the selected location. Please try another location or refine your search.
                //   </Typography>
                // </Box>

                <>
                  {/* <div className="mb-6">
                  <h2 className="text-xl font-bold text-white drop-shadow-lg mb-1">Search Results</h2>
                  <p className="text-white/80 font-medium drop-shadow-md text-sm">
                    No <span className="text-yellow-300 font-semibold">professionals</span> found for the selected location
                    <span className="text-yellow-300 font-semibold">{searchLocation}</span>
                  </p>
                </div> */}

                  {/* Elegant Inline Empty State */}
                  <div className="text-center py-12 px-4">
                    {/* Floating Icons Animation */}
                    <div className="relative mb-8">
                      <div className="flex justify-center items-center space-x-4">
                        <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 animate-pulse">
                          <Users className="w-6 h-6 text-white/70" />
                        </div>
                        <div className="w-16 h-16 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                          <MessageCircle className="w-8 h-8 text-white" strokeWidth={1.5} />
                        </div>
                        <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 animate-pulse delay-500">
                          <MapPin className="w-6 h-6 text-white/70" />
                        </div>
                      </div>

                      {/* Sparkle effects */}
                      <div className="absolute -top-2 left-1/4">
                        <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
                      </div>
                      <div className="absolute -bottom-2 right-1/4">
                        <Sparkles className="w-3 h-3 text-yellow-300 animate-pulse delay-700" />
                      </div>
                    </div>

                    {/* Main Message */}
                    <div className="mb-8">
                      <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight">
                        Did not find what you want?
                      </h3>
                      <p className="text-white/90 text-lg max-w-2xl mx-auto leading-relaxed">
                        Can't find the information you're looking for? Share your requirements and we'll help you within 24
                        hours.
                      </p>
                    </div>

                    {/* Action Buttons - Inline Style */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-lg mx-auto">
                      <Button className="bg-yellow-400 text-black font-bold backdrop-blur-sm border border-white/30 font-medium px-6 py-3 h-auto rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
                        onClick={() => setShowRequirementsSectionPopup(true)}>
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Share Your Requirements
                      </Button>

                      <div className="flex gap-3">
                        <Button
                          variant="ghost"
                          className="text-white/80 hover:text-white hover:bg-white/10 text-sm px-4 py-2 h-auto rounded-lg"
                        >
                          Try Different Area
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-white/80 hover:text-white hover:bg-white/10 text-sm px-4 py-2 h-auto rounded-lg"
                        >
                          Browse Categories
                        </Button>
                      </div>
                    </div>

                    {/* Help Text */}
                    <div className="mt-8 pt-6 border-t border-white/20">
                      <p className="text-white/60 text-sm">
                        <span className="inline-flex items-center">
                          <Plus className="w-4 h-4 mr-1" />
                          We'll notify you when new professionals join in your area
                        </span>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* {loading && <div className="text-center"><Loader /></div>} */}
            {!hasMore && <p className="text-center text-white">No more results</p>}
          </div>
        </section>
      </div>

      {showPopupRequirementsSection && (
        <RequirementsDialog open={showPopupRequirementsSection} onOpenChange={setShowRequirementsSectionPopup} />)}

      {showPopup && (
        <LoginPopup
          onClose={() => setShowPopup(false)}
          redirectUrl={redirectUrl}
        />
      )}</>
  );

}

export default SearchVendor;
